import React, { useMemo, useState } from 'react'
import { useStyles } from './FlyoutStorefrontFilters.styles'
import {
  CustomRangeObject,
  DateRangePickerV2,
  SelectV2,
} from '@labrav/react-components'
import {
  convertToFullDateTime,
  formatDateToISO,
} from '../../../common/utils/meteringHelpers'
import { getRangeDates, stripTime } from '../../../common/utils/getDateRanges'
import { customDateRanges } from '../../../common/utils/meteringConstants'
import { SelectChangeEvent, Typography } from '@mui/material'
import { isValid, format } from 'date-fns'
import { useDispatch, useSelector } from '../../../store'
import { MetricsData } from '../../../modules/storefrontMetrics/reducer'
import {
  getStorefrontMetricsData,
  setStorefrontMetricDomain,
  setStorefrontMetricProduct,
  updateMetricDateRange,
} from '../../../modules/storefrontMetrics/action'

export interface Range {
  startDate: Date
  endDate: Date
}
export const FlyoutStorefrontFilters: React.FC = () => {
  const { classes } = useStyles()
  const today = new Date()
  const dispatch = useDispatch()
  const metricsData = useSelector<MetricsData>(
    state => state.storefrontMetricsData
  )

  const { startDate, endDate } = metricsData.dateRange
  const formattedStartDate = useMemo(() => {
    return convertToFullDateTime(startDate) as unknown as Date
  }, [startDate])

  const formattedEndDate = useMemo(() => {
    return convertToFullDateTime(endDate) as unknown as Date
  }, [endDate])
  const [selectedDomain, setSelectedDomain] = useState(metricsData.domain)
  const [selectedMetricProduct, setSelectedMetricProduct] = useState(
    metricsData.selectedProduct
  )

  const domainOptions = metricsData?.domainNames?.map((domain: string) => ({
    label: domain,
    value: domain,
  }))
  const formatRangeLabel = (label: string, startDate: Date, endDate: Date) => (
    <div className={classes.labelContent}>
      <span className={classes.labelContentTitle}>{label}</span>
      <Typography className={classes.labelSubTitle}>
        {isValid(startDate) ? format(startDate, 'MMM dd') : 'Invalid date'} -
        {isValid(endDate) ? format(endDate, 'MMM dd') : 'Invalid date'}
      </Typography>
    </div>
  )
  const customRanges: CustomRangeObject[] = customDateRanges.map(label => {
    const { startDate, endDate } = getRangeDates(
      label,
      today
      //will uncoment when get the start date
      //insertDates?.invoiceDate
    )
    return {
      label: formatRangeLabel(label, startDate, endDate),
      range: () => ({ startDate, endDate }),
      isSelected(range: Range) {
        const { startDate, endDate } = getRangeDates(
          label,
          today
          //   insertDates?.invoiceDate //uncomment when write the reducer
        )
        return (
          stripTime(range.startDate).getTime() ===
            stripTime(startDate).getTime() &&
          stripTime(range.endDate).getTime() === stripTime(endDate).getTime()
        )
      },
    }
  })
  const handleDateRangeChange = async (range: Range) => {
    const startDateIso = formatDateToISO(range.startDate)
    const endDateIso = formatDateToISO(range.endDate)
    await dispatch(
      updateMetricDateRange({ startDate: startDateIso, endDate: endDateIso })
    )
    if (startDateIso && endDateIso) {
      dispatch(
        getStorefrontMetricsData(
          selectedDomain === 'All domains' ? '' : selectedDomain,
          selectedMetricProduct === 'allListings' ? '' : selectedMetricProduct
        )
      )
    }
  }
  const handleChange = async (event: SelectChangeEvent) => {
    const selectedValue = (event.target.value as string) || ''
    setSelectedDomain(selectedValue)
    await dispatch(setStorefrontMetricDomain(selectedValue))
    dispatch(
      getStorefrontMetricsData(
        selectedValue === 'All domains' ? '' : selectedValue,
        selectedMetricProduct === 'allListings' ? '' : selectedMetricProduct
      )
    )
  }
  const handleProductChange = async (event: SelectChangeEvent) => {
    const selectedValue = event.target.value as string
    setSelectedMetricProduct(selectedValue)
    await dispatch(setStorefrontMetricProduct(selectedValue))
    dispatch(
      getStorefrontMetricsData(
        selectedDomain === 'All domains' ? '' : selectedDomain,
        selectedValue === 'allListings' ? '' : selectedValue
      )
    )
  }

  return (
    <div className={classes.filtersContainer} data-testid="filters-section">
      <div style={{ display: 'flex', gap: '24px' }}>
        <div>
          <SelectV2
            data-testid="metricsData.domain"
            minWidth="208px"
            size="small"
            field={{
              title: 'Domains',
              placeHolder: 'Domains',
              id: 'domain',
              isRequired: false,
              humanReadableKey: 'selectDomain',
              isReadOnly: false,
              isCustom: false,
              defaultValue: selectedDomain || 'All domains',
              dataType: 'string',
              options: domainOptions,
              value: selectedDomain || 'All domains',
            }}
            key={'metricsData.domain'}
            onChange={handleChange}
            isError={false}
            isRequired={false}
          />
        </div>
        <div>
          <SelectV2
            data-testid="metricsData.domain"
            minWidth="208px"
            size="small"
            field={{
              title: 'Listing names',
              placeHolder: 'Listing names',
              id: 'listingNames',
              isRequired: false,
              humanReadableKey: 'selectListing',
              isReadOnly: false,
              isCustom: false,
              defaultValue: selectedMetricProduct || 'All Listings',
              dataType: 'string',
              options: metricsData.productOptions,
              value: selectedMetricProduct || 'All Listings',
            }}
            key={'metricsData.productOptions'}
            onChange={handleProductChange}
            isError={false}
            isRequired={false}
          />
        </div>
      </div>

      <div className={classes.dateRangePickerWrrapper}>
        <DateRangePickerV2
          keyName={'date-range-calander'}
          title={'Date range'}
          onDateRangeChange={handleDateRangeChange}
          customRanges={customRanges}
          initialRange={{
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            key: 'selection',
          }}
        />
      </div>
    </div>
  )
}
